import React, { useState } from "react";
import { useDashboard, useCompleteTask, useUpload } from "@/api/hooks";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { useAuth } from "@/lib/AuthContext";
import TaskCard from "@/components/dashboard/TaskCard";
import TaskModal from "@/components/dashboard/TaskModal";
import ManualReviewSubmissionModal from "@/components/dashboard/ManualReviewSubmissionModal";
import ReferralTaskModal from "@/components/dashboard/ReferralTaskModal";
import { isManualReviewTask, getTaskStatus } from "@/lib/taskUtils";

/**
 * LandingTasksSection
 * Props:
 *   theme - "light" | "dark" | "festival"
 */
export default function LandingTasksSection({ theme = "light" }) {
  const { isAuthenticated } = useAuth();
  const { data, isLoading: loading, refetch: load } = useDashboard();
  const completeTask = useCompleteTask();
  const upload = useUpload();
  const [selectedTask, setSelectedTask] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [optimisticStatuses, setOptimisticStatuses] = useState({});

  // Theme definitions
  const themes = {
    light: {
      sectionBg: "#f4f6f9",
      card: "#ffffff",
      border: "#e5e7eb",
      textPrimary: "#0a0a0a",
      textSecondary: "rgba(0,0,0,0.4)",
      accent: "#16a34a",
      progressTrack: "#e5e7eb",
      headerBg: "#0a0a0a",
      headerBorder: "#1f1f1f",
      headerAccent: "#c8f135",
    },
    dark: {
      sectionBg: "transparent",
      card: "#111111",
      border: "#222222",
      textPrimary: "#ffffff",
      textSecondary: "rgba(255,255,255,0.4)",
      accent: "#c8f135",
      progressTrack: "#222",
      headerBg: "#0d0d0d",
      headerBorder: "#1f1f1f",
      headerAccent: "#c8f135",
    },
    festival: {
      sectionBg: "transparent",
      card: "rgba(255,255,255,0.05)",
      border: "rgba(200,80,255,0.25)",
      textPrimary: "#ffffff",
      textSecondary: "rgba(255,220,255,0.55)",
      accent: "#c850ff",
      progressTrack: "rgba(200,80,255,0.2)",
      headerBg: "rgba(10,0,22,0.9)",
      headerBorder: "rgba(200,80,255,0.3)",
      headerAccent: "#c850ff",
    },
  };

  const t = themes[theme] || themes.light;

  // Build themeVars compatible with TaskCard
  const themeVars = {
    card: t.card,
    border: t.border,
    textPrimary: t.textPrimary,
    textSecondary: t.textSecondary,
    accent: t.accent,
    light: theme === "light",
  };

  const handleTaskSelect = (task) => {
    if (getTaskStatus(task, optimisticStatuses) === "pending_review") return;
    setSelectedTask(task);
  };

  const handleComplete = async (task, submissionData) => {
    setCompleting(true);
    try {
      const res = await completeTask.mutateAsync({
        taskId: task.id,
        submission_data: submissionData,
      });
      setSelectedTask(null);
      const pts = res.points_earned;
      setSuccessMsg(`+${pts} נקודות נוספו! 🎉`);
      setTimeout(() => setSuccessMsg(null), 3500);
      const count = pts >= 200 ? 200 : pts >= 50 ? 120 : 60;
      confetti({ particleCount: count, spread: 80, origin: { y: 0.5 }, colors: ["#c8f135", "#ffffff", "#ff3dac", "#3dffea"] });
    } catch (err) {
      console.error("Failed to complete task:", err);
    }
    setCompleting(false);
  };

  const handleReviewSubmit = async (task, payload) => {
    setCompleting(true);
    setOptimisticStatuses((prev) => ({ ...prev, [task.id]: "pending_review" }));
    setSelectedTask(null);
    setSuccessMsg("המשימה נשלחה לבדיקה");
    setTimeout(() => setSuccessMsg(null), 3500);
    try {
      const uploadRes = await upload.mutateAsync(payload.file);
      await completeTask.mutateAsync({
        taskId: task.id,
        proof_url: uploadRes.url,
        metadata: { reference_text: payload.reference_text },
      });
    } catch (err) {
      console.error("Failed to submit for review:", err);
    }
    setCompleting(false);
  };

  if (!isAuthenticated || loading) return (
    <div className="py-16 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-transparent rounded-full animate-spin"
        style={{ borderTopColor: t.accent }} />
    </div>
  );

  if (!data) return (
    <div className="py-16 text-center" style={{ color: t.textSecondary }}>
      <p>No tasks found.</p>
    </div>
  );

  const { tasks, user: campaignUser } = data;
  if (!campaignUser) return null;
  const max_points = 602;
  const pct = Math.min(100, Math.round(((campaignUser.total_points || 0) / max_points) * 100));
  const pendingTasks = tasks.filter((tk) => !tk.is_completed && tk.task_type !== "referral");
  const completedTasks = tasks.filter((tk) => tk.is_completed);
  const referralTask = tasks.find((tk) => tk.task_type === "referral");

  const ha = t.headerAccent || t.accent;

  return (
    <div dir="rtl" style={{ background: t.sectionBg, paddingBottom: "60px" }}>
      {/* Boarding-pass style header — always dark */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden"
        style={{ background: t.headerBg, borderBottom: `1px solid ${t.headerBorder}` }}
      >
        {/* Decorative glow blob */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 rounded-full pointer-events-none"
          style={{ background: `${ha}15`, filter: "blur(40px)" }} />

        <div className="max-w-2xl mx-auto px-5 pt-8 pb-6 relative z-10">
          {/* Top row: name + rank badge */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: "rgba(255,255,255,0.35)" }}>
                    שלום, {campaignUser.display_name} 👋
              </p>
              <p className="font-black text-white text-xl leading-none">המשימות שלי</p>
            </div>
            {/* Rank pill */}
            <div className="flex flex-col items-center px-4 py-2 rounded-2xl"
              style={{ background: `${ha}20`, border: `1.5px solid ${ha}40` }}>
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: `${ha}99` }}>דירוג</span>
              <span className="font-black text-2xl leading-none" style={{ color: ha }}>#{campaignUser.rank}</span>
              <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.3)" }}>מ-{campaignUser.total_participants}</span>
            </div>
          </div>

          {/* Points display */}
          <div className="flex items-end gap-3 mb-5">
            <span className="font-black leading-none" style={{ fontSize: "clamp(3rem,10vw,4.5rem)", color: ha }}>
              {campaignUser.total_points}
            </span>
            <div className="pb-1">
              <span className="text-base font-bold" style={{ color: `${ha}60` }}>/ {max_points}</span>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.3)" }}>נקודות</p>
            </div>
          </div>

          {/* Progress bar — segmented */}
          <div className="rounded-full h-3 overflow-hidden mb-2" style={{ background: "rgba(255,255,255,0.08)" }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${ha}, ${ha}bb)` }}
            />
          </div>
          <div className="flex justify-between items-center">
            <p className="text-xs font-bold" style={{ color: `${ha}80` }}>{pct}% הושלם</p>
            <p className="text-xs font-bold" style={{ color: "rgba(255,255,255,0.25)" }}>{max_points - campaignUser.total_points} נק' לסיום</p>
          </div>
        </div>

        {/* Ticket perforation bottom edge */}
        <div className="flex justify-between px-2 pb-3 relative z-10">
          {Array.from({ length: 28 }).map((_, i) => (
            <div key={i} className="w-2 h-2 rounded-full" style={{ background: t.sectionBg || "#f4f6f9", opacity: 0.6 }} />
          ))}
        </div>
      </motion.div>

      <div className="max-w-2xl mx-auto px-4 pt-6 space-y-6 overflow-hidden">

        {/* Success toast */}
        <AnimatePresence>
          {successMsg && (
            <motion.div
              key="toast"
              initial={{ opacity: 0, scale: 0.85, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85 }}
              className="text-center py-3 px-6 rounded-full font-black text-sm"
              style={{ background: `${t.accent}22`, color: t.accent, border: `1px solid ${t.accent}44` }}
            >
              {successMsg}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Referral task */}
        {referralTask && (
          <div className="mb-6">
            <TaskCard
              task={referralTask}
              index={0}
              status="open"
              onSelect={() => handleTaskSelect(referralTask)}
              themeVars={themeVars}
            />
          </div>
        )}

        {/* Pending tasks */}
        {pendingTasks.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px flex-1" style={{ background: theme === "light" ? "#e5e7eb" : "#222" }} />
              <p className="text-[11px] font-black uppercase tracking-widest px-2" style={{ color: t.textSecondary }}>
                משימות פתוחות
              </p>
              <div className="h-px flex-1" style={{ background: theme === "light" ? "#e5e7eb" : "#222" }} />
            </div>
            <div className="space-y-3">
              {pendingTasks.map((task, i) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  index={i}
                  status={getTaskStatus(task, optimisticStatuses)}
                  onSelect={() => handleTaskSelect(task)}
                  themeVars={themeVars}
                />
              ))}
            </div>
          </div>
        )}

        {/* Completed tasks */}
        {completedTasks.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px flex-1" style={{ background: theme === "light" ? "#e5e7eb" : "#222" }} />
              <p className="text-[11px] font-black uppercase tracking-widest px-2" style={{ color: t.textSecondary }}>
                הושלמו ✓
              </p>
              <div className="h-px flex-1" style={{ background: theme === "light" ? "#e5e7eb" : "#222" }} />
            </div>
            <div className="space-y-3">
              {completedTasks.map((task, i) => (
                <TaskCard key={task.id} task={task} index={i} completed status="completed" themeVars={themeVars} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {selectedTask && getTaskStatus(selectedTask, optimisticStatuses) !== "pending_review" && (
          selectedTask.task_type === "referral" ? (
            <ReferralTaskModal
              key={selectedTask.id}
              task={selectedTask}
              referralToken={campaignUser.referral_code}
              onClose={() => setSelectedTask(null)}
            />
          ) : isManualReviewTask(selectedTask) ? (
            <ManualReviewSubmissionModal
              key={selectedTask.id}
              task={selectedTask}
              submitting={completing}
              onClose={() => setSelectedTask(null)}
              onSubmit={handleReviewSubmit}
            />
          ) : (
            <TaskModal
              key={selectedTask.id}
              task={selectedTask}
              completing={completing}
              onClose={() => setSelectedTask(null)}
              onComplete={handleComplete}
            />
          )
        )}
      </AnimatePresence>
    </div>
  );
}
